import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from './useAuth'
import api from '../services/api'

/**
 * useNavBadges — polls the backend for unread notification summary
 * and returns a map of { '/projects': 'green', '/contracts': 'amber', ... }
 *
 * Also exposes `markSectionRead(prefix)` which is called when the user
 * navigates to a section — clears its dot immediately.
 */
export function useNavBadges() {
  const { user, isAuthenticated } = useAuth()
  const [badges, setBadges] = useState({})
  const intervalRef = useRef(null)

  const fetchBadges = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const r = await api.get('/notifications/unread-summary/')
      // r.data = { '/projects': { color, link, title }, ... }
      setBadges(r.data || {})
    } catch {
      // non-fatal
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated) { setBadges({}); return }
    fetchBadges()
    // Re-poll every 30 seconds to pick up new notifications
    intervalRef.current = setInterval(fetchBadges, 30_000)
    return () => clearInterval(intervalRef.current)
  }, [isAuthenticated, fetchBadges])

  const markSectionRead = useCallback(async (linkPrefix) => {
    // Optimistically remove the badge immediately
    setBadges(prev => {
      const next = { ...prev }
      delete next[linkPrefix]
      return next
    })
    try {
      await api.put(`/notifications/mark-section-read/?link_prefix=${encodeURIComponent(linkPrefix)}`)
    } catch {
      // non-fatal
    }
  }, [])

  return { badges, markSectionRead, refresh: fetchBadges }
}
